import React, { useState, useEffect, useRef } from "react";
import { Card, Row, Col, message } from "antd";
import axios from "axios";
import { useHistory, useLocation } from "react-router-dom";
import { useAuth0 } from "../../react-auth0-spa";
import { useAppContext } from "../../context";
import config from "../../api_config.json";
import { competitors, partners } from "../../config";

// Import componenti e funzioni ausiliarie
import { clienteCoordsQuery, buildOfcQuotesQuery } from "./queries/index";
import { filterQuotes, calculateQuoteStats } from "./quotesUtils";
import QuoteSidePanel from "./QuoteSidePanel";
import QuoteMapContent from "./QuoteMapContent";
import LoadingSpinner from "./LoadingSpinner";
import { parseQuoteFilters, serializeQuoteFilters } from "./quoteFiltersUrl";

export default function MappaPreventivi() {
  const history = useHistory();
  const location = useLocation();
  const initialFiltersRef = useRef(null);
  const didReadInitialSearchRef = useRef(false);
  const syncingFromUrlRef = useRef(false);

  if (!initialFiltersRef.current) {
    initialFiltersRef.current = parseQuoteFilters(location.search);
  }

  const initialFilters = initialFiltersRef.current;

  // Data states
  const [clienteCoords, setClienteCoords] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [filteredQuotes, setFilteredQuotes] = useState([]);
  
  // Loading states
  const [loadingCoords, setLoadingCoords] = useState(true);
  const [loadingQuotes, setLoadingQuotes] = useState(true);
  const [filtering, setFiltering] = useState(false);
  const [coordsError, setCoordsError] = useState(null);
  const [quotesError, setQuotesError] = useState(null);
  
  // Filter states
  const [statusFilter, setStatusFilter] = useState(initialFilters.statusFilter);
  const [dateRange, setDateRange] = useState(initialFilters.dateRange);
  const [useDateRange, setUseDateRange] = useState(initialFilters.useDateRange);
  const [subtotalMin, setSubtotalMin] = useState(initialFilters.subtotalMin);
  const [useSubtotalFilter, setUseSubtotalFilter] = useState(initialFilters.useSubtotalFilter);
  
  // Proximity filter states
  const [useProximityFilter, setUseProximityFilter] = useState(initialFilters.useProximityFilter);
  const [proximityRange, setProximityRange] = useState(initialFilters.proximityRange);
  const [debouncedProximityRange, setDebouncedProximityRange] = useState(initialFilters.proximityRange);
  const [userLocation, setUserLocation] = useState(initialFilters.userLocation);
  const [loadingLocation, setLoadingLocation] = useState(false);
  const [isSelectingLocation, setIsSelectingLocation] = useState(false);
  const [customLocation, setCustomLocation] = useState(initialFilters.customLocation);
  const [useCustomLocation, setUseCustomLocation] = useState(initialFilters.useCustomLocation);
  
  // Statistics state for global and filtered counts
  const [stats, setStats] = useState({
    all: 0,       // Total count of all quotes
    accettato: 0, // Count of accepted quotes
    bozza: 0,     // Count of draft quotes 
    inviato: 0,   // Count of sent quotes
    pagato: 0,    // Count of paid quotes
    rifiutato: 0, // Count of rejected quotes
    totalAmount: 0 // Sum of filtered quotes amounts
  });
  
  // Toggles for competitors and partners
  const [showCompetitors, setShowCompetitors] = useState(initialFilters.showCompetitors);
  const [showPartners, setShowPartners] = useState(initialFilters.showPartners);
  
  const mapRef = useRef(null);
  const { getTokenSilently } = useAuth0();
  const { isProd } = useAppContext();
  const env = isProd ? "prod" : "dev";
  
  // Debounce function for proximity range changes
  useEffect(() => {
    const debounceTimeout = setTimeout(() => {
      setDebouncedProximityRange(proximityRange);
    }, 500); // 500ms debounce delay
    
    return () => {
      clearTimeout(debounceTimeout);
    };
  }, [proximityRange]);

  // Keep filters in sync when users navigate with browser history.
  useEffect(() => {
    if (!didReadInitialSearchRef.current) {
      didReadInitialSearchRef.current = true;
      return;
    }

    syncingFromUrlRef.current = true;
    const filters = parseQuoteFilters(location.search);

    setStatusFilter(filters.statusFilter);
    setDateRange(filters.dateRange);
    setUseDateRange(filters.useDateRange);
    setSubtotalMin(filters.subtotalMin);
    setUseSubtotalFilter(filters.useSubtotalFilter);
    setUseProximityFilter(filters.useProximityFilter);
    setProximityRange(filters.proximityRange);
    setDebouncedProximityRange(filters.proximityRange);
    setUserLocation(filters.userLocation);
    setCustomLocation(filters.customLocation);
    setUseCustomLocation(filters.useCustomLocation);
    setShowCompetitors(filters.showCompetitors);
    setShowPartners(filters.showPartners);

    const resetSyncFlag = setTimeout(() => {
      syncingFromUrlRef.current = false;
    }, 0);

    return () => {
      clearTimeout(resetSyncFlag);
    };
  }, [location.search]);

  // Write active filters to the querystring for deeplinks and back/forward.
  useEffect(() => {
    if (syncingFromUrlRef.current) return;

    const nextSearch = serializeQuoteFilters({
      statusFilter,
      dateRange,
      useDateRange,
      subtotalMin,
      useSubtotalFilter,
      useProximityFilter,
      proximityRange: debouncedProximityRange,
      userLocation,
      customLocation,
      useCustomLocation,
      showCompetitors,
      showPartners,
    });
    const currentSearch = location.search.startsWith("?")
      ? location.search.slice(1)
      : location.search;

    if (nextSearch !== currentSearch) {
      history.push(`${location.pathname}${nextSearch ? `?${nextSearch}` : ""}`);
    }
  }, [
    statusFilter,
    dateRange,
    useDateRange,
    subtotalMin,
    useSubtotalFilter,
    useProximityFilter,
    debouncedProximityRange,
    userLocation,
    customLocation,
    useCustomLocation,
    showCompetitors,
    showPartners,
    history,
    location.pathname,
    location.search,
  ]);

  // Fetch cliente coordinates once; quote filters are applied in a separate query.
  useEffect(() => {
    let isMounted = true;

    const fetchClienteCoords = async () => {
      try {
        setLoadingCoords(true);
        const token = await getTokenSilently();
        const graphqlUrl = `${config.baseUrl[env]}/graphql?raw=true`;

        const coordsResponse = await axios({
          method: "post",
          url: graphqlUrl,
          data: { query: clienteCoordsQuery },
          headers: { Authorization: `Bearer ${token}` },
        });

        if (!isMounted) return;

        if (coordsResponse.data?.cliente_coords?.edges) {
          const clientesWithCoords = coordsResponse.data.cliente_coords.edges
            .map(edge => edge.node)
            .filter(coord => 
              coord.latitude && 
              coord.longitude && 
              coord.cliente_pointer
          );
          
          setClienteCoords(clientesWithCoords);
          setCoordsError(null);
        } else {
          setCoordsError("Nessuna coordinata trovata");
        }
      } catch (error) {
        if (!isMounted) return;
        console.error("Error fetching cliente coordinates:", error);
        setCoordsError("Errore nel caricamento dei dati");
      } finally {
        if (isMounted) {
          setLoadingCoords(false);
        }
      }
    };

    fetchClienteCoords();

    return () => {
      isMounted = false;
    };
  }, [getTokenSilently, env]);

  // Fetch quotes again when filters that can run on GraphQL change.
  useEffect(() => {
    let isMounted = true;

    const fetchQuotes = async () => {
      try {
        setLoadingQuotes(true);
        const token = await getTokenSilently();
        const graphqlUrl = `${config.baseUrl[env]}/graphql?raw=true`;
        const query = buildOfcQuotesQuery({
          statusFilter,
          dateRange,
          useDateRange,
          subtotalMin,
          useSubtotalFilter,
        });

        const quotesResponse = await axios({
          method: "post",
          url: graphqlUrl,
          data: { query },
          headers: { Authorization: `Bearer ${token}` },
        });

        if (!isMounted) return;

        if (quotesResponse.data?.errors?.length) {
          throw new Error(quotesResponse.data.errors[0].message);
        }

        if (quotesResponse.data?.ofc_quotes?.edges) {
          const quotesData = quotesResponse.data.ofc_quotes.edges
            .map(edge => edge.node)
            .filter(quote => quote.customer_pointer);

          setQuotes(quotesData);
          setStats(calculateQuoteStats(quotesData));
          setQuotesError(null);
        } else {
          setQuotes([]);
          setStats(calculateQuoteStats([]));
          setQuotesError("Nessun preventivo trovato");
        }
      } catch (error) {
        if (!isMounted) return;
        console.error("Error fetching quotes:", error);
        setQuotes([]);
        setStats(calculateQuoteStats([]));
        setQuotesError("Errore nel caricamento dei preventivi");
      } finally {
        if (isMounted) {
          setLoadingQuotes(false);
        }
      }
    };

    fetchQuotes();

    return () => {
      isMounted = false;
    };
  }, [
    getTokenSilently,
    env,
    statusFilter,
    dateRange,
    useDateRange,
    subtotalMin,
    useSubtotalFilter,
  ]);

  // Apply filters when they change
  useEffect(() => {
    if (quotes.length === 0 || clienteCoords.length === 0) {
      setFilteredQuotes([]);
      setStats(calculateQuoteStats([]));
      setFiltering(false);
      return;
    }
    
    // Set filtering state for UI feedback
    setFiltering(true);

    // Use requestAnimationFrame for smooth UI
    const animationFrame = requestAnimationFrame(() => {
      const filtered = filterQuotes(
        quotes,
        statusFilter,
        false, // useStatusFilter non serve più
        dateRange,
        useDateRange,
        subtotalMin,
        useSubtotalFilter,
        clienteCoords,
        useProximityFilter,
        debouncedProximityRange,
        userLocation,
        useCustomLocation,
        customLocation
      );
      
      setFilteredQuotes(filtered);
      
      // Ricalcola tutte le statistiche con i preventivi filtrati
      setStats(calculateQuoteStats(filtered));
      
      // Short timeout for visual feedback
      setTimeout(() => {
        setFiltering(false);
      }, 300);
    });

    return () => {
      cancelAnimationFrame(animationFrame);
    };
  }, [
    quotes,
    clienteCoords,
    statusFilter,
    dateRange,
    useDateRange,
    subtotalMin,
    useSubtotalFilter,
    useProximityFilter,
    debouncedProximityRange,
    userLocation,
    useCustomLocation,
    customLocation
  ]);

  // Filter change handlers
  const handleStatusChange = (value) => {
    setStatusFilter(value);
  };

  const handleDateRangeChange = (dates) => {
    setDateRange(dates);
  };

  const handleSubtotalMinChange = (value) => {
    setSubtotalMin(value);
  };

  const handleProximityRangeChange = (value) => {
    if (value !== null) {
      setProximityRange(value);
    }
  };

  // Get user's geolocation
  const getUserLocation = () => {
    setLoadingLocation(true);
    
    if (!navigator.geolocation) {
      message.error("Geolocalizzazione non supportata dal tuo browser");
      setLoadingLocation(false);
      return;
    }
    
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const userCoords = {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude
        };
        setUserLocation(userCoords);
        setUseProximityFilter(true);
        
        // Center map on user location if map reference exists
        if (mapRef.current) {
          mapRef.current.setView(
            [userCoords.latitude, userCoords.longitude],
            mapRef.current.getZoom()
          );
        }
        
        message.success("Posizione rilevata correttamente");
        setLoadingLocation(false);
      },
      (error) => {
        console.error("Error getting location:", error);
        message.error("Impossibile ottenere la tua posizione. Verifica le autorizzazioni.");
        setLoadingLocation(false);
      },
      { maximumAge: 60000, timeout: 5000, enableHighAccuracy: true }
    );
  };

  // Toggle use of user's location
  const useMyLocation = () => {
    if (!userLocation) {
      getUserLocation();
    }
    setUseCustomLocation(false);
    setIsSelectingLocation(false);
    setUseProximityFilter(true);
    
    // If user's location is already available, center the map on it
    if (userLocation && mapRef.current) {
      mapRef.current.setView(
        [userLocation.latitude, userLocation.longitude],
        mapRef.current.getZoom()
      );
    }
  };
  
  // Start custom location selection mode
  const startCustomLocationSelection = () => {
    message.info('Clicca un punto sulla mappa per impostare il centro del raggio');
    setIsSelectingLocation(true);
    setUseCustomLocation(true);
    setUseProximityFilter(true);
  };
  
  // Handle map click to set custom location
  const handleMapClick = (event) => {
    setCustomLocation({
      lat: event.latlng.lat,
      lng: event.latlng.lng
    });
    setIsSelectingLocation(false);
    message.success('Posizione personalizzata impostata correttamente');
  };

  const isInitialLoading = loadingCoords || (loadingQuotes && quotes.length === 0);
  const isFiltering = filtering || loadingQuotes;
  const error = coordsError || quotesError;

  if (isInitialLoading) {
    return <LoadingSpinner />;
  }

  return (
    <Card
      title="Mappa Preventivi"
      extra={error && <span style={{ color: "red" }}>{error}</span>}
      styles={{ body: { padding: '12px' } }}
    >
      <Row gutter={[16, 16]}>
        {/* Layout a due colonne responsive */}
        <Col xs={24} md={8} lg={6}>
          <QuoteSidePanel 
            statusFilter={statusFilter}
            dateRange={dateRange}
            useDateRange={useDateRange}
            subtotalMin={subtotalMin}
            useSubtotalFilter={useSubtotalFilter}
            useProximityFilter={useProximityFilter}
            proximityRange={proximityRange}
            userLocation={userLocation}
            customLocation={customLocation}
            useCustomLocation={useCustomLocation}
            isSelectingLocation={isSelectingLocation}
            loadingLocation={loadingLocation}
            filteredQuotes={filteredQuotes}
            stats={stats}
            handleStatusChange={handleStatusChange}
            handleDateRangeChange={handleDateRangeChange}
            handleSubtotalMinChange={handleSubtotalMinChange}
            handleProximityRangeChange={handleProximityRangeChange}
            setUseDateRange={setUseDateRange}
            setUseSubtotalFilter={setUseSubtotalFilter}
            setUseProximityFilter={setUseProximityFilter}
            useMyLocation={useMyLocation}
            startCustomLocationSelection={startCustomLocationSelection}
            filtering={isFiltering}
            showCompetitors={showCompetitors}
            setShowCompetitors={setShowCompetitors}
            showPartners={showPartners}
            setShowPartners={setShowPartners}
          />
        </Col>
        
        {/* Mappa sulla destra */}
        <Col xs={24} md={16} lg={18}>
          <QuoteMapContent 
            filteredQuotes={filteredQuotes}
            quoteCoords={clienteCoords}
            customLocation={customLocation}
            useCustomLocation={useCustomLocation}
            isSelectingLocation={isSelectingLocation}
            proximityRange={proximityRange}
            handleMapClick={handleMapClick}
            mapRef={mapRef}
            filtering={isFiltering}
            useProximityFilter={useProximityFilter}
            userLocation={userLocation}
            showCompetitors={showCompetitors}
            competitors={competitors}
            showPartners={showPartners}
            partners={partners}
          />
        </Col>
      </Row>
    </Card>
  );
}
